<template>
    <v-card
        elevation="0"
        :outlined="$vuetify.breakpoint.smAndUp ? true : false"
        max-width="730"
        class="pa-8 mb-10 crowdfunding-tab"
    >
        <v-card-text class="pb-0">
            <!-- Page Header -->
            <v-row>
                <v-col>
                    <h3>Crowdfunding</h3>
                    <p class="page-subtitle mt-2">
                        Let's start college on the right note. Spread the word and get friends and family to help out!
                    </p>
                </v-col>
            </v-row>

            <!-- Stripe Not Connected -->
            <div v-if="!isUserOnboarded" class="py-4">
                <v-card elevation="0" outlined class="pa-6 text-center stripe-card">
                    <v-icon size="48" color="grey lighten-1" class="mb-4">mdi-credit-card-outline</v-icon>

                    <!-- User has partial Stripe setup -->
                    <template v-if="user.stripe">
                        <h4 class="mb-3">Finish your Stripe onboarding</h4>
                        <p class="mb-4 grey--text">You're almost there! Complete your Stripe onboarding to start your campaign.</p>
                        <v-btn color="primary" depressed @click="finishOnboarding" :loading="isConnectingStripe">
                            Finish Onboarding
                        </v-btn>
                    </template>

                    <!-- User has no Stripe at all -->
                    <template v-else>
                        <h4 class="mb-3">Connect to Stripe to Get Started</h4>
                        <p class="mb-4 grey--text">To start a fundraising campaign and receive donations, you'll need to connect your Stripe account.</p>
                        <AddStripeAccountDialog />
                    </template>
                </v-card>
            </div>

            <!-- Has Active Campaign -->
            <template v-else-if="activeCampaign">
                <!-- Campaign Management Header -->
                <div class="py-4">
                    <v-card elevation="0" class="management-card pa-5">
                        <v-row>
                            <v-col cols="12" md="8">
                                <div class="d-flex align-center mb-3">
                                    <v-chip color="success" small class="mr-3">
                                        <v-icon left x-small>mdi-circle</v-icon>
                                        Active
                                    </v-chip>
                                    <span class="campaign-goal-text">Goal: ${{ activeCampaign.amount }}</span>
                                </div>
                                <div class="management-info">
                                    <div class="info-item">
                                        <v-icon size="18" color="grey darken-1" class="mr-2">mdi-calendar-clock</v-icon>
                                        <span class="info-label">Funds arrive:</span>
                                        <span class="info-value">
                                            {{ activeCampaign.deposit_type === 'delay' ? campaignEndDate : 'When goal is reached' }}
                                        </span>
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="12" md="4" class="d-flex flex-column justify-center">
                                <v-btn
                                    color="primary"
                                    depressed
                                    class="mb-2"
                                    @click="showEditCampaignDialog = true"
                                >
                                    <v-icon left small>mdi-pencil</v-icon>
                                    Edit Campaign
                                </v-btn>
                                <v-btn
                                    outlined
                                    color="success"
                                    @click="showEndCampaignDialog = true"
                                >
                                    <v-icon left small>mdi-cash-check</v-icon>
                                    Claim Funds
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card>
                </div>

                <!-- Progress Section -->
                <div class="py-4">
                    <CampaignProgress
                        :donations="activeCampaign.donations"
                        :goal="activeCampaign.amount"
                    />
                </div>

                <!-- Share Section -->
                <div class="py-4">
                    <CampaignShare
                        :share-url="campaignUrl"
                        :user-name="user.name"
                        title="Share with others"
                        description="The more you share, the easier and faster it will be to reach your goal."
                    />
                </div>

                <!-- Recent Donations -->
                <div class="py-4">
                    <CampaignDonations
                        :donations="activeCampaign.donations"
                        title="Recent Activity"
                        empty-message="No donations yet. Share your campaign to start receiving donations!"
                        :is-owner="true"
                    />
                </div>
            </template>

            <!-- No Campaign Yet -->
            <div v-else class="py-4">
                <v-card elevation="0" outlined class="pa-6 text-center no-campaign-card">
                    <v-icon size="48" color="grey lighten-1" class="mb-4">mdi-bullhorn-outline</v-icon>
                    <h4 class="mb-3">You haven't started a campaign yet</h4>
                    <p class="mb-4 grey--text">When you create a campaign, it will appear here.</p>
                    <v-btn
                        color="primary"
                        depressed
                        @click="showCampaignDialog = true"
                        width="200"
                    >
                        Start a Campaign
                    </v-btn>
                </v-card>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Campaign History Section -->
            <v-row>
                <v-col>
                    <h3>Campaign History</h3>
                    <p class="page-subtitle mt-2">View your past fundraising campaigns</p>
                </v-col>
            </v-row>

            <!-- Loading State -->
            <v-row v-if="isLoadingHistory">
                <v-col class="text-center py-8">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        size="48"
                    ></v-progress-circular>
                    <p class="mt-4 grey--text">Loading campaigns...</p>
                </v-col>
            </v-row>

            <!-- Empty History State -->
            <v-row v-else-if="!hasCompletedCampaigns">
                <v-col>
                    <v-card elevation="0" outlined class="pa-6 text-center empty-card">
                        <v-icon size="48" color="grey lighten-1" class="mb-4">mdi-history</v-icon>
                        <h4 class="mb-3">No campaign history yet</h4>
                        <p class="mb-0 grey--text">When your campaigns end, they will appear here.</p>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Campaigns List -->
            <v-row v-else>
                <v-col>
                    <v-card elevation="0" outlined class="campaigns-list-card pa-4">
                        <template v-for="(campaign, index) in completedCampaigns">
                            <v-divider v-if="index > 0" :key="'divider-' + index" class="my-4"></v-divider>
                            <div
                                :key="campaign.id"
                                class="campaign-item"
                                @click="viewCampaign(campaign.hash)"
                            >
                                <v-row align="center">
                                    <v-col cols="auto">
                                        <v-avatar
                                            color="primary"
                                            size="44"
                                            class="campaign-avatar"
                                        >
                                            <v-icon color="white" size="24">mdi-bullhorn</v-icon>
                                        </v-avatar>
                                    </v-col>
                                    <v-col>
                                        <div class="d-flex align-center flex-wrap mb-1">
                                            <span class="campaign-hash mr-3">{{ campaign.hash }}</span>
                                            <v-chip color="success" x-small dark>
                                                Completed
                                            </v-chip>
                                        </div>
                                        <div class="campaign-meta">
                                            <v-icon size="14" class="mr-1">mdi-calendar</v-icon>
                                            {{ formatDate(campaign.created_at) }}
                                            <span class="mx-2">|</span>
                                            Goal: ${{ campaign.amount }}
                                        </div>
                                        <div class="raised-amount mt-1">
                                            ${{ getTotalDonations(campaign.donations) }} raised
                                        </div>
                                        <div class="completion-date mt-1">
                                            <v-icon size="14" color="success" class="mr-1">mdi-check-circle</v-icon>
                                            Completed on {{ formatDate(campaign.updated_at) }}
                                        </div>
                                    </v-col>
                                    <v-col cols="auto" class="d-none d-sm-flex">
                                        <v-btn
                                            outlined
                                            small
                                            color="primary"
                                            class="btn-edurent"
                                            @click.stop="viewCampaign(campaign.hash)"
                                        >
                                            View
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </div>
                        </template>
                    </v-card>
                </v-col>
            </v-row>

            <!-- FAQ Section -->
            <div class="py-4 mt-4" v-if="faqPage && faqPage.length">
                <FaqCollapsible :page="faqPage" />
            </div>
        </v-card-text>

        <!-- Create Campaign Dialog -->
        <v-dialog v-model="showCampaignDialog" max-width="540">
            <Campaign
                @hideDialog="showCampaignDialog = false"
                @getCampaign="getCampaign"
            />
        </v-dialog>

        <!-- Edit Campaign Dialog -->
        <v-dialog v-model="showEditCampaignDialog" max-width="540">
            <EditCampaign
                v-if="activeCampaign"
                :campaign="activeCampaign"
                @hideDialog="showEditCampaignDialog = false"
                @getCampaign="getCampaign"
            />
        </v-dialog>

        <!-- End Campaign Dialog -->
        <v-dialog v-model="showEndCampaignDialog" max-width="540">
            <EndCampaign
                v-if="activeCampaign"
                :campaign="activeCampaign"
                @hideDialog="showEndCampaignDialog = false"
                @getCampaign="getCampaign"
            />
        </v-dialog>
    </v-card>
</template>

<script>
import moment from 'moment'
import Campaign from '@/components/Campaign.vue'
import EditCampaign from '@/components/EditCampaign.vue'
import EndCampaign from '@/components/EndCampaign.vue'
import CampaignProgress from '@/components/campaign/CampaignProgress.vue'
import CampaignShare from '@/components/campaign/CampaignShare.vue'
import CampaignDonations from '@/components/campaign/CampaignDonations.vue'
import FaqCollapsible from '@/components/FaqCollapsible.vue'
import AddStripeAccountDialog from '@/components/AddStripeAccountDialog.vue'
import UserService from '@/services/user.service'
import SiteService from '@/services/site.service'
import PaymentService from '@/services/payment.service'

export default {
    name: 'CrowdfundingTab',
    components: {
        Campaign,
        EditCampaign,
        EndCampaign,
        CampaignProgress,
        CampaignShare,
        CampaignDonations,
        FaqCollapsible,
        AddStripeAccountDialog
    },
    props: {
        activeUser: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            showCampaignDialog: false,
            showEditCampaignDialog: false,
            showEndCampaignDialog: false,
            activeCampaign: null,
            isCallingApi: false,
            isConnectingStripe: false,
            isLoadingHistory: true,
            faqPage: []
        }
    },
    computed: {
        user() {
            return this.$store.getters.getUser
        },
        campaigns() {
            return this.$store.getters.getCampaigns || []
        },
        completedCampaigns() {
            return this.campaigns.filter(c => c.is_ended === 1)
        },
        hasCompletedCampaigns() {
            return this.completedCampaigns.length > 0
        },
        campaignEndDate() {
            if (!this.user.university_start_date) return ''
            return moment(this.user.university_start_date).subtract(14, 'd').format('L')
        },
        isUserOnboarded() {
            return this.user.stripe && this.user.stripe.onboarding_completed
        },
        campaignUrl() {
            if (!this.activeCampaign) return ''
            return `${process.env.VUE_APP_CLIENT_BASE_URL}/campaign/${this.activeCampaign.hash}`
        }
    },
    watch: {
        user(value) {
            if (value && Object.keys(value).length > 0) {
                this.getCampaign()
            }
        }
    },
    methods: {
        async getCampaign() {
            this.isCallingApi = true
            this.activeCampaign = await UserService.getCampaign(this.user.id, '?appendUser&appendDonations')
            this.isCallingApi = false
        },
        async finishOnboarding() {
            this.isConnectingStripe = true
            try {
                const accountLink = await PaymentService.getAccountLink()
                if (accountLink && accountLink.url) {
                    window.location.href = accountLink.url
                }
            } catch {
                this.$store.dispatch('getUserByToken')
            }
            this.isConnectingStripe = false
        },
        async loadCampaigns() {
            this.isLoadingHistory = true
            await this.$store.dispatch('getCampaigns')
            this.isLoadingHistory = false
        },
        formatDate(value) {
            if (value) {
                return moment(String(value)).format('LL')
            }
            return ''
        },
        getTotalDonations(donations) {
            if (!donations || donations.length === 0) return '0.00'
            let total = 0
            donations.forEach(donation => {
                total += Number(donation.amount)
            })
            return (total / 100).toFixed(2)
        },
        viewCampaign(hash) {
            this.$router.push(`/donate?cmp=${hash}`)
        }
    },
    async beforeMount() {
        this.getCampaign()
        this.loadCampaigns()
        this.faqPage = await SiteService.getMyFundsPage()
    }
}
</script>

<style>
.crowdfunding-tab h3 {
    font-size: 26px !important;
    font-weight: 700;
    color: #000;
}

.crowdfunding-tab h4 {
    font-size: 18px;
    font-weight: 700;
    color: #333;
}

.crowdfunding-tab .page-subtitle {
    font-size: 14px;
    color: #666;
    margin: 0;
}

.crowdfunding-tab .stripe-card,
.crowdfunding-tab .no-campaign-card,
.crowdfunding-tab .empty-card {
    border-radius: 10px !important;
}

.crowdfunding-tab .management-card {
    border-radius: 12px !important;
    background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
    border: 1px solid #e9ecef !important;
}

.crowdfunding-tab .campaign-goal-text {
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.crowdfunding-tab .management-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.crowdfunding-tab .info-item {
    display: flex;
    align-items: center;
    font-size: 14px;
}

.crowdfunding-tab .info-label {
    color: #666;
    margin-right: 6px;
}

.crowdfunding-tab .info-value {
    color: #333;
    font-weight: 500;
}

.crowdfunding-tab .campaigns-list-card {
    border-radius: 10px !important;
}

.crowdfunding-tab .campaign-item {
    padding: 12px 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.crowdfunding-tab .campaign-item:hover {
    background-color: #f8f9fa;
}

.crowdfunding-tab .campaign-avatar {
    flex-shrink: 0;
}

.crowdfunding-tab .campaign-hash {
    font-family: 'Roboto Mono', monospace;
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.crowdfunding-tab .campaign-meta {
    font-size: 13px;
    color: #666;
    display: flex;
    align-items: center;
}

.crowdfunding-tab .raised-amount {
    font-size: 15px;
    font-weight: 600;
    color: #40B771;
}

.crowdfunding-tab .completion-date {
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
}

@media (max-width: 600px) {
    .crowdfunding-tab .campaign-hash {
        font-size: 14px;
    }

    .crowdfunding-tab .campaign-meta {
        font-size: 12px;
        flex-wrap: wrap;
    }

    .crowdfunding-tab .raised-amount {
        font-size: 14px;
    }
}
</style>
